import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaGridItem } from './MediaGridItem';

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface ContributionItem {
  id: string;
  size: number;
  category_id?: string; // Keep for backward compatibility
  category_ids?: string[]; // New field for multiple categories
  reviewed: boolean;
  title: string;
  description: string;
  duration?: number;
  timestamp?: string;
  location?: Coordinates;
  release_rights: string;
  creator: string;
  language: string;
  file_hash: string;
  snr_frequency: number;
}

interface UserContributions {
  totalContributions: number;
  contributionsByType: {
    text: number;
    audio: number;
    image: number;
    video: number;
    document: number;
  };
  audioContributions: ContributionItem[];
  videoContributions: ContributionItem[];
  textContributions: ContributionItem[];
  imageContributions: ContributionItem[];
  documentContributions: ContributionItem[];
  audioDuration: number;
  videoDuration: number;
}

export interface Category {
  id: string;
  name: string;
  title: string;
  description: string;
  published: boolean;
  rank: number;
  created_at: string;
  updated_at: string;
}

export interface ContributionsListProps {
  contributions: UserContributions | null;
  selectedMediaType: 'text' | 'audio' | 'video' | 'image' | 'document' | null;
  token: string;
  isOwnProfile: boolean;
  categories?: Category[];
  categoriesLoading?: boolean;
  categoriesError?: string | null;
}

export const ContributionsList: React.FC<ContributionsListProps> = ({
  contributions,
  selectedMediaType,
  token,
  isOwnProfile,
  categories = [],
  categoriesLoading = false,
  categoriesError = null,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const itemsPerPage = 20;

  if (!contributions || !selectedMediaType) return null;

  let rawItems: ContributionItem[] = [];
  if (selectedMediaType === 'text')
    rawItems = contributions.textContributions || [];
  if (selectedMediaType === 'audio')
    rawItems = contributions.audioContributions || [];
  if (selectedMediaType === 'video')
    rawItems = contributions.videoContributions || [];
  if (selectedMediaType === 'image')
    rawItems = contributions.imageContributions || [];
  if (selectedMediaType === 'document')
    rawItems = contributions.documentContributions || [];

  if (!rawItems || rawItems.length === 0) {
    return (
      <div className="text-center text-gray-400 py-8 text-lg font-medium">
        No{' '}
        {selectedMediaType.charAt(0).toUpperCase() + selectedMediaType.slice(1)}{' '}
        {t('stats.contributionsYet')}
      </div>
    );
  }

  // Filter items by selected category
  let filteredItems = [...rawItems];
  if (selectedCategoryId) {
    filteredItems = filteredItems.filter((item) => {
      if (item.category_id === selectedCategoryId) {
        return true;
      }
      if (
        item.category_ids &&
        Array.isArray(item.category_ids) &&
        item.category_ids.includes(selectedCategoryId)
      ) {
        return true;
      }
      return false;
    });
  }

  // Sort items by timestamp (created_at)
  const sortedItems = [...filteredItems].sort((a, b) => {
    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;

    if (sortOrder === 'newest') {
      return timeB - timeA;
    } else {
      return timeA - timeB;
    }
  });

  // Calculate pagination
  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedItems.slice(startIndex, endIndex);

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategoryId((prev) => (prev === categoryId ? null : categoryId));
    setCurrentPage(1);
  };

  const handleSortChange = (order: 'newest' | 'oldest') => {
    setSortOrder(order);
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Category Filter and Sort Toggle Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-100">
        {/* Category Chips */}
        <div className="flex-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {t('common.filterByCategory')}
          </p>
          {categoriesLoading && (
            <div className="flex items-center gap-2 py-1.5">
              <div className="w-4 h-4 rounded-full border-2 border-blue-600 border-t-transparent animate-spin"></div>
              <span className="text-xs text-gray-500">
                Loading categories...
              </span>
            </div>
          )}
          {categoriesError && (
            <span className="text-xs text-red-500">{categoriesError}</span>
          )}
          {!categoriesLoading && !categoriesError && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setSelectedCategoryId(null);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 border ${
                  selectedCategoryId === null
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent'
                }`}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 border ${
                    selectedCategoryId === category.id
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent'
                  }`}
                >
                  {category.title || category.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sort Controls */}
        <div className="flex-shrink-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:text-right">
            {t('common.sortOrder')}
          </p>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm">
            <button
              onClick={() => handleSortChange('newest')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                sortOrder === 'newest'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t('common.newest.first')}
            </button>
            <button
              onClick={() => handleSortChange('oldest')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                sortOrder === 'oldest'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t('common.oldest.first')}
            </button>
          </div>
        </div>
      </div>

      {sortedItems.length === 0 ? (
        <div className="text-center text-gray-400 py-12 text-lg font-medium">
          {t('common.noContributionsFoundMatchingThisCategory')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {currentItems.map((item) => (
              <MediaGridItem
                key={item.id}
                item={item}
                mediaType={selectedMediaType}
                token={token}
              />
            ))}
          </div>
          {/* Pagination controls */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
};

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // If total pages is less than or equal to max visible, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, last page, current page, and adjacent pages
      if (currentPage <= 3) {
        // Near the beginning
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push(1);
        pages.push('ellipsis');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Somewhere in the middle
        pages.push(1);
        pages.push('ellipsis');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex flex-wrap items-center justify-center mt-4 sm:mt-6 gap-1 sm:gap-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg border text-sm ${
          currentPage === 1
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
        }`}
      >
        Prev
      </button>

      {pageNumbers.map((page, index) => (
        <React.Fragment key={index}>
          {page === 'ellipsis' ? (
            <span className="px-2 py-1.5 sm:px-3 sm:py-2 text-gray-500 text-sm">
              ...
            </span>
          ) : (
            <button
              onClick={() => onPageChange(page as number)}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg border text-sm ${
                currentPage === page
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
              }`}
            >
              {page}
            </button>
          )}
        </React.Fragment>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg border text-sm ${
          currentPage === totalPages
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
        }`}
      >
        Next
      </button>
    </div>
  );
};
